import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaChartLine, FaArrowUp, FaArrowDown, FaCheck, FaTimes } from 'react-icons/fa';
import theme from '../styles/theme';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BacktestContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  padding: ${theme.spacing.lg};
  margin-top: ${theme.spacing.lg};
`;

const BacktestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const BacktestTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSize.lg};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const ChartContainer = styled.div`
  height: 250px;
  margin-bottom: ${theme.spacing.lg};
  position: relative;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
`;

const SummaryCard = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSize.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const SummaryValue = styled.div`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSize.lg};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const Value = styled.span.withConfig({
  shouldForwardProp: (prop) => !['positive', 'negative'].includes(prop),
})`
  color: ${props => {
    if (props.positive) return theme.colors.status.success;
    if (props.negative) return theme.colors.status.error;
    return theme.colors.text.primary;
  }};
`;

const EmptyState = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.text.muted};
`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 8
  }).format(value);
};

const formatPercent = (value) => {
  return `${value.toFixed(8)}%`;
};

// Smooth scroll helper
const scrollToBottom = () => {
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  });
};

const BacktestResults = ({ results }) => {
  // Scroll into view when results appear
  useEffect(() => {
    if (results && results.data) {
      scrollToBottom();
    }
  }, [results]);

  if (!results || !results.data) {
    return (
      <BacktestContainer>
        <EmptyState>No backtest data available</EmptyState>
      </BacktestContainer>
    );
  }

  const { data, summary } = results;
  const isProfitable = summary.totalPnl > 0;

  const renderChart = () => {
    const chartData = {
      labels: data.map(d => d.date),
      datasets: [
        {
          label: 'Price',
          data: data.map(d => d.price),
          fill: false,
          borderColor: theme.colors.accent,
          tension: 0.1,
          pointRadius: data.map(d => d.trades && d.trades.length > 0 ? 8 : 2),
          pointBackgroundColor: data.map(d => {
            if (!d.trades || d.trades.length === 0) return theme.colors.accent;
            const trade = d.trades[0];
            if (trade.type === 'entry') return trade.side === 'long' ? '#27AE60' : '#E74C3C';
            if (trade.type === 'exit') return trade.pnl > 0 ? '#27AE60' : '#E74C3C';
            return theme.colors.accent;
          }),
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)'
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            afterLabel: function(context) {
              const dataPoint = data[context.dataIndex];
              if (dataPoint.trades && dataPoint.trades.length > 0) {
                const trade = dataPoint.trades[0];
                if (trade.type === 'entry') {
                  return `${trade.side.toUpperCase()} ENTRY`;
                } else if (trade.type === 'exit') {
                  return `EXIT - PnL: $${trade.pnl.toFixed(2)}`;
                }
              }
              return '';
            }
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  return (
    <BacktestContainer>
      <BacktestHeader>
        <BacktestTitle>
          <FaChartLine /> Backtest Results
        </BacktestTitle>
      </BacktestHeader>

      <ChartContainer>
        {renderChart()}
      </ChartContainer>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Total Profit/Loss</SummaryLabel>
          <SummaryValue>
            {isProfitable ? <FaArrowUp color={theme.colors.status.success} /> : <FaArrowDown color={theme.colors.status.error} />}
            <Value positive={isProfitable} negative={!isProfitable}>
              {formatCurrency(summary.totalPnl)}
            </Value>
          </SummaryValue>
        </SummaryCard>

        <SummaryCard>
          <SummaryLabel>Return Percentage</SummaryLabel>
          <SummaryValue>
            <Value positive={summary.pnlPercent > 0} negative={summary.pnlPercent < 0}>
              {formatPercent(summary.pnlPercent)}
            </Value>
          </SummaryValue>
        </SummaryCard>

        <SummaryCard>
          <SummaryLabel>Win Rate</SummaryLabel>
          <SummaryValue>
            {summary.winRate > 50 ? <FaCheck color={theme.colors.status.success} /> : <FaTimes color={theme.colors.status.error} />}
            <Value positive={summary.winRate > 50} negative={summary.winRate <= 50}>
              {formatPercent(summary.winRate)}
            </Value>
          </SummaryValue>
        </SummaryCard>

        <SummaryCard>
          <SummaryLabel>Average Trade</SummaryLabel>
          <SummaryValue>
            <Value positive={summary.averageTrade > 0} negative={summary.averageTrade < 0}>
              {formatCurrency(summary.averageTrade)}
            </Value>
          </SummaryValue>
        </SummaryCard>
      </SummaryGrid>
    </BacktestContainer>
  );
};

export default BacktestResults; 